import { ASSET_OPTIONS } from '@/util/constants';
import { putMultipleFundingSourceForClientByClientIdRequest } from '@client/model/clientModel';
import { selectClient, selectSession, selectSessionAdvisor } from '@client/model/selectors';
import { AppState } from '@client/model/store';
import { SessionType, isNullOrUndefined } from '@shared';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

export interface AccountDetailFormState {
  existingAssetsValue: number | null;
  monthlyContributionAmount: number | null;
  annualRateOfReturn: number | null;
  gainsTaxRate: number | null;
}

type SelfFundingFormField = keyof AccountDetailFormState;

type AccountDetailFormErrors = Partial<Record<SelfFundingFormField, string>>;

const REQUIRED_FIELDS: SelfFundingFormField[] = [
  'existingAssetsValue',
  'annualRateOfReturn',
  'gainsTaxRate',
];

const DEFAULT_VALUES: AccountDetailFormState = {
  existingAssetsValue: null,
  monthlyContributionAmount: null,
  annualRateOfReturn: null,
  gainsTaxRate: null,
};

export function useAccountDetailForm(fundingSourceId: string | null, accountType?: string) {
  const dispatch = useDispatch();

  const client = useSelector(selectClient);
  const { sessionType } = useSelector(selectSession);
  const { advisorId } = useSelector(selectSessionAdvisor);
  const {
    clientId,
    appliedInferenceSet: { ltcAtYear },
  } = client;

  const isAdvisorSession =
    sessionType === SessionType.advisor || sessionType === SessionType.wizard;

  const isSaving = useSelector((state: { client: AppState['client'] }) => state.client.isLoading);

  // Get existing self-funding source directly from client state by fundingSourceId
  const existingSelfFundingSource = fundingSourceId
    ? client.multipleFundingSources?.selfFunding?.[fundingSourceId]
    : undefined;

  // Convert SelfFundingSource to form state (handling 0 as null for empty display)
  const getInitialFormState = useCallback((): AccountDetailFormState => {
    if (!existingSelfFundingSource) {
      return DEFAULT_VALUES;
    }
    return {
      existingAssetsValue: existingSelfFundingSource.existingAssetsValue ?? null,
      monthlyContributionAmount: existingSelfFundingSource.monthlyContributionAmount ?? null,
      annualRateOfReturn: existingSelfFundingSource.annualRateOfReturn ?? null,
      gainsTaxRate: existingSelfFundingSource.gainsTaxRate ?? null,
    };
  }, [existingSelfFundingSource]);

  // Local form state
  const [formState, setFormState] = useState<AccountDetailFormState>(getInitialFormState);
  const [initialFormState, setInitialFormState] =
    useState<AccountDetailFormState>(getInitialFormState);
  const [formErrors, setFormErrors] = useState<AccountDetailFormErrors>({});
  const [formHasBeenSubmitted, setFormHasBeenSubmitted] = useState(false);

  // Validate form and return whether it's valid
  const validateForm = useCallback(() => {
    const errors: AccountDetailFormErrors = {};

    REQUIRED_FIELDS.forEach(field => {
      const value = formState[field as keyof AccountDetailFormState];
      if (isNullOrUndefined(value)) {
        errors[field] = 'Required Field';
      }
    });

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  }, [formState]);

  // Re-validate when form has been submitted and fields change
  useEffect(() => {
    if (formHasBeenSubmitted) {
      validateForm();
    }
  }, [
    formState.existingAssetsValue,
    formState.annualRateOfReturn,
    formState.gainsTaxRate,
    formHasBeenSubmitted,
    validateForm,
  ]);

  useEffect(() => {
    const newState = getInitialFormState();
    setFormState(newState);
    setInitialFormState(newState);
    setFormHasBeenSubmitted(false);
    setFormErrors({});
  }, [fundingSourceId, getInitialFormState]);

  // Curried change handler (same pattern as SelfFundingDetailsModal)
  const handleChangeField = useCallback(
    (field: SelfFundingFormField) => (value: number | null) => {
      setFormState(prev => ({
        ...prev,
        [field]: value,
      }));
    },
    [],
  );

  // Submit handler - validates and saves to Redux + Backend
  const handleSubmit = useCallback(() => {
    setFormHasBeenSubmitted(true);
    const isValid = validateForm();

    if (!isValid) {
      return false;
    }

    // Get the account type from existing source, or use passed accountType for new source
    const sourceAccountType = existingSelfFundingSource?.accountType || accountType || null;
    const accountTypeLabel = sourceAccountType
      ? ASSET_OPTIONS.find(asset => asset.type === sourceAccountType)?.label
      : null;

    const accountName =
      existingSelfFundingSource?.selfFundingName ||
      accountTypeLabel ||
      accountType ||
      sourceAccountType ||
      'Self-funding';

    // Create self-funding data structure for backend
    const updatedSelfFunding = {
      hasSelfFunding: true,
      selfFundingName: accountName,
      accountType: sourceAccountType || accountName,
      existingAssetsValue: formState.existingAssetsValue ?? 0,
      annualRateOfReturn: formState.annualRateOfReturn ?? 0,
      contributionStartYear: new Date().getFullYear(),
      contributionEndYear: ltcAtYear,
      monthlyContributionAmount: formState.monthlyContributionAmount ?? 0,
      gainsTaxRate: formState.gainsTaxRate ?? 0,
      monthlyIncome: null,
      contributionMonthCount: 0,
      ...(isAdvisorSession && { addedByAdvisorId: advisorId }),
    };

    // Save to backend - null for create, actual ID for update
    dispatch(
      putMultipleFundingSourceForClientByClientIdRequest({
        clientId: clientId,
        fundingSourceType: 'selfFunding',
        fundingSourceData: updatedSelfFunding as any,
        fundingSourceId: fundingSourceId, // null = create, ID = update
      }),
    );

    return true;
  }, [
    fundingSourceId,
    existingSelfFundingSource,
    dispatch,
    formState,
    validateForm,
    ltcAtYear,
    isAdvisorSession,
    advisorId,
    clientId,
  ]);

  // Reset form to initial state
  const resetForm = useCallback(() => {
    setFormState(initialFormState);
    setFormErrors({});
    setFormHasBeenSubmitted(false);
  }, [initialFormState]);

  // Check if form has unsaved changes
  const isDirty =
    formState.existingAssetsValue !== initialFormState.existingAssetsValue ||
    formState.monthlyContributionAmount !== initialFormState.monthlyContributionAmount ||
    formState.annualRateOfReturn !== initialFormState.annualRateOfReturn ||
    formState.gainsTaxRate !== initialFormState.gainsTaxRate;

  // Check if form is currently valid (for UI purposes)
  const isValid = Object.keys(formErrors).length === 0;

  // Check if there are required field errors
  const hasRequiredFieldErrors = REQUIRED_FIELDS.some(field => formErrors[field]);

  return {
    formState,
    formErrors,
    formHasBeenSubmitted,
    isDirty,
    isValid,
    hasRequiredFieldErrors,
    isSaving,
    handleChangeField,
    handleSubmit,
    resetForm,
  };
}
